/**
 * Integer primitives shared by everything in the core.
 *
 * Every function here is specified in spec/core-spec.md and must behave bit for bit
 * the same in every port. All arithmetic is exact: 32-bit wrapping where the spec says so,
 * otherwise plain integers below 2^53 (which JavaScript doubles represent exactly).
 */

/**
 * Mixes three integers into one 32-bit value. Inputs are interpreted as 32-bit integers
 * (two's complement), so negative coordinates and unsigned seeds both work. Returns an
 * unsigned 32-bit integer in [0, 2^32).
 */
export function mix32(a: number, b: number, c: number): number {
  let h =
    (Math.imul(a | 0, 0x27d4eb2d) ^ Math.imul(b | 0, 0x165667b1) ^ Math.imul(c | 0, 0x9e3779b1)) |
    0;
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  h ^= h >>> 16;
  return h >>> 0;
}

/** Division rounding toward negative infinity. `b` must be positive. */
export function floorDiv(a: number, b: number): number {
  return Math.floor(a / b);
}

/** Remainder in [0, b) for positive `b`, matching `floorDiv`. */
export function floorMod(a: number, b: number): number {
  return a - floorDiv(a, b) * b;
}

/** Exact integer square root: the largest integer r with r * r <= n. For 0 <= n < 2^53. */
export function isqrt(n: number): number {
  if (n < 2) return n;
  let r = Math.floor(Math.sqrt(n));
  while (r * r > n) r -= 1;
  while ((r + 1) * (r + 1) <= n) r += 1;
  return r;
}
